import { useEffect, useState } from "react";
import { Copy, Check, X, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "cv_crios_install_hint_dismissed:v1";
// Small grace period so the viewer isn't hit with a modal on first paint.
const SHOW_DELAY_MS = 2500;

function isChromeOnIos() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  // CriOS is the tell for Chrome on iOS. It piggybacks on WebKit and can't
  // Add-to-Home-Screen — the viewer has to open the URL in Safari first.
  if (!/CriOS/i.test(ua)) return false;
  // Belt & braces: only iOS-flavoured UAs.
  return /iPhone|iPad|iPod/i.test(ua);
}

/**
 * Modal shown to Chrome-on-iOS visitors explaining that installing requires
 * opening the page in Safari first. Offers a one-tap "Copy link" so the
 * viewer can paste into Safari's address bar. Dismissal is persisted.
 */
export default function ChromeIosInstallHint() {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Force-show path: dispatched by ``usePwaInstall.install()`` when a
    // CriOS viewer taps an "Install Cinevoya" affordance elsewhere.
    // Also fires for generic iOS in-app browsers (Instagram, FB, etc.)
    // which get platform='ios-chrome' for the same reason.
    const onForceShow = (e) => {
      if (e?.detail?.platform && e.detail.platform !== "ios-chrome") return;
      setVisible(true);
    };
    window.addEventListener("cv:show-install-hint", onForceShow);

    if (!isChromeOnIos()) return () => window.removeEventListener("cv:show-install-hint", onForceShow);
    try {
      if (window.localStorage.getItem(DISMISS_KEY)) {
        return () => window.removeEventListener("cv:show-install-hint", onForceShow);
      }
    } catch { /* private tab — still show once */ }
    const t = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => {
      clearTimeout(t);
      window.removeEventListener("cv:show-install-hint", onForceShow);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { window.localStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
  };

  const copyLink = async () => {
    const url = window.location.origin || "https://cinevoya.com";
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for older CriOS builds without Clipboard API.
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      // If clipboard is blocked we just leave the URL visible in the modal.
    }
  };

  if (!visible) return null;

  const url = window.location.origin || "https://cinevoya.com";

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm animate-in fade-in"
        onClick={dismiss}
        aria-hidden="true"
      />
      <div
        className="fixed left-2 right-2 bottom-2 z-50 rounded-2xl bg-surface border border-white/10 shadow-2xl shadow-black/60 p-5 animate-in slide-in-from-bottom-5 sm:left-1/2 sm:right-auto sm:bottom-6 sm:-translate-x-1/2 sm:max-w-md"
        role="dialog"
        aria-label="Install Cinevoya via Safari"
        data-testid="crios-install-hint"
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close install hint"
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-foreground/50 hover:text-foreground hover:bg-white/5"
          data-testid="crios-install-dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <Compass className="w-4 h-4 text-terracotta" />
          <p className="text-xs uppercase tracking-[0.25em] text-terracotta font-semibold">Open in Safari</p>
        </div>
        <h3 className="font-display text-lg font-bold mb-2">Install Cinevoya on your iPhone</h3>
        <p className="text-sm text-foreground/80 leading-relaxed mb-4">
          To install Cinevoya on your iPhone, open this page in <strong className="text-foreground/95">Safari</strong>,
          tap the <strong className="text-foreground/95">Share</strong> icon at the bottom of the screen,
          then select <strong className="text-foreground/95">Add to Home Screen</strong>.
        </p>

        <div className="rounded-lg bg-black/30 border border-white/10 px-3 py-2 text-xs text-foreground/70 font-mono truncate mb-3" data-testid="crios-install-url">
          {url}
        </div>

        <Button
          type="button"
          onClick={copyLink}
          className="w-full h-10 gap-2 rounded-full bg-terracotta hover:bg-terracotta/90 text-white text-sm font-semibold"
          data-testid="crios-install-copy"
        >
          {copied ? (
            <>
              <Check className="w-4 h-4" />
              Link copied — open Safari and paste
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              Copy Cinevoya link
            </>
          )}
        </Button>
      </div>
    </>
  );
}
